import { localStorage } from '@gitbook/util-local-storage';

import welcomeStep1Video from './assets/site-centric-welcome-step-1.mp4';
import welcomeStep1Poster from './assets/site-centric-welcome-step-1-poster.webp';
import welcomeStep2Video from './assets/site-centric-welcome-step-2.mp4';
import welcomeStep2Poster from './assets/site-centric-welcome-step-2-poster.webp';
import welcomeStep3Video from './assets/site-centric-welcome-step-3.mp4';
import welcomeStep3Poster from './assets/site-centric-welcome-step-3-poster.webp';
import welcomeStep4Video from './assets/site-centric-welcome-step-4.mp4';
import welcomeStep4Poster from './assets/site-centric-welcome-step-4-poster.webp';

/** Local storage key that controls whether the site-centric welcome dialog should open. */
export const SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY = 'shouldShowSiteCentricWelcomeDialog';

/** Video sources used by the site-centric welcome dialog steps, ordered by step. */
export const SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES = [
    welcomeStep1Video,
    welcomeStep2Video,
    welcomeStep3Video,
    welcomeStep4Video,
] as const;

/** Poster sources shown while site-centric welcome dialog videos load, ordered by step. */
export const SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES = [
    welcomeStep1Poster,
    welcomeStep2Poster,
    welcomeStep3Poster,
    welcomeStep4Poster,
] as const;

/** Assets preloading to ensure instant display when the site-centric welcome dialog opens */

const preloadedVideos: HTMLVideoElement[] = [];
const mediaPreparationPromises = new Map<string, Promise<void>>();
const warmedVideos = new Map<string, Promise<void>>();
let preloadLinksHaveBeenAdded = false;
let preloadedSiteCentricWelcomeDialogMedia: Promise<void> | undefined;
const DEFAULT_INITIAL_MEDIA_WAIT = 1000;
const FIRST_ENABLE_INITIAL_MEDIA_WAIT = 5000;

/**
 * Preload the site-centric welcome dialog videos so playback can start as soon as the dialog opens.
 */
export function preloadSiteCentricWelcomeDialogMedia(): Promise<void> {
    addSiteCentricWelcomeDialogPreloadLinks();

    if (preloadedSiteCentricWelcomeDialogMedia) {
        return preloadedSiteCentricWelcomeDialogMedia;
    }

    preloadedSiteCentricWelcomeDialogMedia = Promise.all([
        ...SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES.map((src) => warmMediaSource(src)),
        ...SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES.map((src) => warmVideoSource(src)),
    ]).then(() => undefined);

    return preloadedSiteCentricWelcomeDialogMedia;
}

/**
 * Preload enough site-centric welcome dialog media to show the dialog quickly.
 */
export async function preloadSiteCentricWelcomeDialogInitialMedia(
    maxWaitMs = DEFAULT_INITIAL_MEDIA_WAIT
): Promise<void> {
    void preloadSiteCentricWelcomeDialogMedia();

    await Promise.race([
        preloadSiteCentricWelcomeDialogFirstStepMedia(),
        waitForSiteCentricWelcomeDialogMediaTimeout(maxWaitMs),
    ]);
}

/**
 * Release prepared site-centric welcome dialog media after the dialog is dismissed.
 */
export function releaseSiteCentricWelcomeDialogMedia(): void {
    preloadedVideos.forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.load();
    });
    preloadedVideos.length = 0;

    warmedVideos.clear();
    mediaPreparationPromises.clear();
    preloadedSiteCentricWelcomeDialogMedia = undefined;
}

function addSiteCentricWelcomeDialogPreloadLinks(): void {
    if (preloadLinksHaveBeenAdded) {
        return;
    }

    preloadLinksHaveBeenAdded = true;

    SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES.forEach((src) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        document.head.appendChild(link);
    });

    SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES.forEach((src) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'video';
        link.href = src;
        document.head.appendChild(link);
    });
}

function preloadSiteCentricWelcomeDialogFirstStepMedia(): Promise<void> {
    return Promise.all([
        warmMediaSource(SITE_CENTRIC_WELCOME_DIALOG_POSTER_SOURCES[0]),
        warmVideoSource(SITE_CENTRIC_WELCOME_DIALOG_VIDEO_SOURCES[0]),
    ]).then(() => undefined);
}

/**
 * Mark the site-centric welcome dialog to open after switching to the new navigation.
 */
export async function prepareSiteCentricWelcomeDialog(): Promise<void> {
    const storedValue = localStorage.get<boolean>(SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY);

    if (storedValue === false) {
        return;
    }

    await localStorage.set(SITE_CENTRIC_WELCOME_DIALOG_STORAGE_KEY, true);
    // Intentionally wait for the first-step media during the transition overlay so the welcome
    // dialog can start playback immediately when the overlay disappears.
    await preloadSiteCentricWelcomeDialogInitialMedia(
        storedValue === true ? DEFAULT_INITIAL_MEDIA_WAIT : FIRST_ENABLE_INITIAL_MEDIA_WAIT
    );
}

function warmMediaSource(src: string): Promise<void> {
    const existingPromise = mediaPreparationPromises.get(src);
    if (existingPromise) {
        return existingPromise;
    }

    const promise = fetch(src, { cache: 'force-cache' })
        .then(async (response) => {
            if (!response.ok) {
                return;
            }

            await response.blob();
        })
        .catch(() => undefined);
    mediaPreparationPromises.set(src, promise);

    return promise;
}

function warmVideoSource(src: string, video?: HTMLVideoElement): Promise<void> {
    const existingPromise = warmedVideos.get(src);
    if (existingPromise) {
        return existingPromise;
    }

    const targetVideo = video ?? document.createElement('video');
    targetVideo.preload = 'auto';
    targetVideo.muted = true;
    targetVideo.playsInline = true;
    targetVideo.src = src;

    if (!preloadedVideos.includes(targetVideo)) {
        preloadedVideos.push(targetVideo);
    }

    const promise = new Promise<void>((resolve) => {
        if (targetVideo.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
            resolve();
            return;
        }

        const finish = () => {
            window.clearTimeout(timeout);
            targetVideo.removeEventListener('canplay', finish);
            targetVideo.removeEventListener('loadeddata', finish);
            targetVideo.removeEventListener('error', finish);
            resolve();
        };
        const timeout = window.setTimeout(finish, 5000);

        targetVideo.addEventListener('canplay', finish, { once: true });
        targetVideo.addEventListener('loadeddata', finish, { once: true });
        targetVideo.addEventListener('error', finish, { once: true });
        targetVideo.load();
    });
    warmedVideos.set(src, promise);

    return promise;
}

function waitForSiteCentricWelcomeDialogMediaTimeout(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
